import React, { useEffect, useState, useRef } from 'react';
import LoadError from '../../components/LoadError';
import { formatDate } from '../../lib/utils';
import { apiGet, apiUpload, apiDelete, API_BASE } from '../../contexts/AuthContext';
import { FileText, Upload, Download, Trash2, FileCheck, FilePlus } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const gridStagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const cardUp = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } } };

const TEMPLATE_CATEGORIES = [
  { name: 'Letter of Recommendation', icon: FileText, color: 'text-brand', bg: 'bg-brand/10' },
  { name: 'Medium of Instruction', icon: FileCheck, color: 'text-success', bg: 'bg-brand/10' },
  { name: 'No Backlog Certificate', icon: FileText, color: 'text-iris', bg: 'bg-brand/10' },
  { name: 'Statement of Purpose', icon: FileText, color: 'text-aqua', bg: 'bg-brand/10' },
  { name: 'Affidavits', icon: FileText, color: 'text-danger', bg: 'bg-ember/10' },
];

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [uploading, setUploading] = useState(null);
  const fileInputRefs = useRef({});

  const fetchTemplates = () => {
    setLoadError(false);
    apiGet('/admin/templates')
      .then(r => setTemplates(r.data))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchTemplates(); }, []);

  const handleUpload = async (category, file) => {
    if (!file) return;
    setUploading(category);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await apiUpload(`/admin/templates?category=${encodeURIComponent(category)}`, formData);
      toast.success(`Template uploaded for ${category}`);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(null);
    }
  };

  const handleDownload = async (template) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/templates/${template.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = template.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Delete "${template.filename}" from ${template.category}?`)) return;
    try {
      await apiDelete(`/admin/templates/${template.id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const getTemplatesForCategory = (category) => templates.filter(t => t.category === category);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="templates-page">
      <div className="flex items-center gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-ember tracking-tight" style={{ fontFamily: 'Outfit' }}>Templates</h1>
          <p className="text-ink-2 mt-1">Manage document templates for partner and candidate use</p>
        </div>
      </div>

      {loadError ? <LoadError what="the templates" onRetry={() => { setLoading(true); fetchTemplates(); }} /> : (
      <motion.div variants={gridStagger} initial="hidden" animate="show" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {TEMPLATE_CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          const catTemplates = getTemplatesForCategory(cat.name);
          const isUploading = uploading === cat.name;

          return (
            <motion.div variants={cardUp}
              key={cat.name}
              className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 p-6 hover:shadow-sm transition-shadow"
              data-testid={`template-card-${cat.name.toLowerCase().replace(/\s/g, '-')}`}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cat.bg}`}>
                    <Icon className={`w-5 h-5 ${cat.color}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-ink text-sm">{cat.name}</h3>
                    <p className="text-xs text-ink-3">
                      {catTemplates.length} {catTemplates.length === 1 ? 'file' : 'files'} uploaded
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => fileInputRefs.current[cat.name]?.click()}
                  disabled={isUploading}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-contrast/[0.07] hover:bg-contrast/10 text-ink transition-colors disabled:opacity-50"
                  data-testid={`upload-btn-${cat.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {isUploading ? (
                    <div className="w-3.5 h-3.5 border-2 border-brand border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Upload className="w-3.5 h-3.5" />
                  )}
                  Upload
                </button>
                <input
                  type="file"
                  ref={el => fileInputRefs.current[cat.name] = el}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xlsx,.xls,.ppt,.pptx,.txt,.rtf"
                  onChange={(e) => {
                    handleUpload(cat.name, e.target.files[0]);
                    e.target.value = '';
                  }}
                />
              </div>

              {/* File List */}
              {catTemplates.length > 0 ? (
                <div className="space-y-2">
                  {catTemplates.map((t) => (
                    <div
                      key={t.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-contrast/5 border border-contrast/[0.07] group"
                      data-testid={`template-file-${t.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <FileText className="w-4 h-4 text-ink-3 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-ink truncate">{t.filename}</p>
                          <p className="text-xs text-ink-3">
                            {formatFileSize(t.size)} &middot; {formatDate(t.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <button
                          onClick={() => handleDownload(t)}
                          className="p-1.5 rounded-md hover:bg-surface-3 text-ink-2 hover:text-brand transition-colors"
                          title="Download"
                          data-testid={`download-btn-${t.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(t)}
                          className="p-1.5 rounded-md hover:bg-ember/10 text-ink-2 hover:text-danger transition-colors"
                          title="Delete"
                          data-testid={`delete-btn-${t.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-6 text-center">
                  <FilePlus className="w-8 h-8 text-ink-3 mb-2" />
                  <p className="text-sm text-ink-3">No template uploaded yet</p>
                  <p className="text-xs text-ink-3 mt-0.5">Click Upload to add a file</p>
                </div>
              )}
            </motion.div>
          );
        })}
      </motion.div>
      )}
    </div>
  );
}
